import { useMemo } from 'react';
import { useAgbTimeSeries } from './useAgbTimeSeries';
import { useAgbChangeByOwnership } from './useAgbChangeByOwnership';
import {
  AgbChangeOwnershipRow,
  AgbTimeSeriesPoint,
  SupplyShedDriveTime,
} from '../lib/types';
import {
  AGB_CHANGE_BASELINE_YEARS,
  AGB_CHANGE_LATEST_YEARS,
} from '../lib/agbChangeColors';

// Honesty constraint (load-bearing — see CLAUDE.md):
//   The Forest Ownership raster (USFS RDS-2025-0045) reports broad ownership
//   *type* at 10 m. It does NOT encode SFI / FSC / PEFC certification status.
//   The card derived from this hook describes family + corporate ownership
//   as "private timberland" — never "certified." Renaming this concept
//   without joining a real certification dataset would silently mis-frame
//   the figure for the audience.

export interface PrivateTimberlandCarbonMaintenance {
  // Five-year smoothing windows. Surfaced so the card's caption can read
  // "mean(2021-25) − mean(2000-04)" without re-importing the constants.
  baselineYears: readonly number[];
  latestYears: readonly number[];

  // The family + corporate rollup row from the per-ownership crosstab —
  // mean change Mg/ha + total acres + gain/stable/loss share.
  allPrivate: AgbChangeOwnershipRow;
  // Family / Corporate rows broken out so the card can show them side by
  // side. Either may be null if the precompute returned zero acres for
  // that class in this shed.
  family:    AgbChangeOwnershipRow | null;
  corporate: AgbChangeOwnershipRow | null;

  // Full shed-mean AGB time series — feeds the card's sparkline. The
  // smoothed-endpoint product is computed offline; this series is just
  // for visual context (annual updates demonstrate the time series alongside
  // the stable change-layer headline).
  sparkline: AgbTimeSeriesPoint[];
  source: 'live';
}

export interface UsePrivateTimberlandCarbonMaintenanceResult {
  data: PrivateTimberlandCarbonMaintenance | null;
  isLoading: boolean;
  error: string | null;
}

export const usePrivateTimberlandCarbonMaintenance = (
  millCode: string | null,
  driveTime: SupplyShedDriveTime,
  enabled: boolean = true,
): UsePrivateTimberlandCarbonMaintenanceResult => {
  const agb = useAgbTimeSeries(millCode, driveTime, enabled);
  const cross = useAgbChangeByOwnership(millCode, driveTime, enabled);

  const data = useMemo<PrivateTimberlandCarbonMaintenance | null>(() => {
    if (!agb.data || !cross.data) return null;
    if (!cross.data.allPrivate) return null;

    return {
      baselineYears: AGB_CHANGE_BASELINE_YEARS,
      latestYears: AGB_CHANGE_LATEST_YEARS,
      allPrivate: cross.data.allPrivate,
      family: cross.data.byClass['family'] ?? null,
      corporate: cross.data.byClass['corporate'] ?? null,
      sparkline: agb.data.points,
      source: 'live',
    };
  }, [agb.data, cross.data]);

  return {
    data,
    isLoading: agb.isLoading || cross.isLoading,
    error: agb.error ?? cross.error,
  };
};
